import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { UseCaseError } from "@/application/errors/UseCaseError";
import { useCreateBeneficiary } from "@/application/queries";
import { buildBeneficiaryWizardRequest } from "@/application/mappers/beneficiaries/beneficiaryWizardMapper";
import { type CreateBeneficiaryResponse, keepActivitiesForIncentive } from "@/domain/beneficiary";
import {
  BENEFICIARY_WIZARD_STEP_IDS,
  type BeneficiaryValidationMessages,
  type BeneficiaryWizardFormData,
  createBeneficiaryWizardSchema,
  EMPTY_BENEFICIARY_WIZARD,
  WIZARD_STEP_FIELDS,
} from "@/domain/validation/beneficiarySchemas";
import { areFieldsValid } from "@/domain/validation/stepValidity";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { fail, succeed, type WorkflowOutcome } from "../shared/outcome";

type Outcome = WorkflowOutcome<CreateBeneficiaryResponse>;

export function useAddBeneficiary({
  open,
  messages,
}: {
  open: boolean;
  messages: BeneficiaryValidationMessages;
}) {
  const orgId = useOrganizationStore((state) => state.selectedOrganizationUuid ?? "");
  const create = useCreateBeneficiary(orgId);
  const schema = useMemo(() => createBeneficiaryWizardSchema(messages), [messages]);
  const form = useForm<BeneficiaryWizardFormData>({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: EMPTY_BENEFICIARY_WIZARD,
  });
  const [step, setStep] = useState(0);
  const submitting = useRef(false);
  const values = form.watch();
  const stepId = BENEFICIARY_WIZARD_STEP_IDS[step];
  const last = step === BENEFICIARY_WIZARD_STEP_IDS.length - 1;

  const reset = useCallback(() => {
    form.reset(EMPTY_BENEFICIARY_WIZARD);
    setStep(0);
  }, [form]);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (name !== "incentiveType") return;
      const activities = form.getValues("activities");
      const kept = keepActivitiesForIncentive(activities, form.getValues("incentiveType"));
      if (kept.length !== activities.length) form.setValue("activities", kept);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const next = async () => {
    if (!stepId || last) return;
    if (await form.trigger([...WIZARD_STEP_FIELDS[stepId]])) setStep(step + 1);
  };

  const back = () => setStep((current) => Math.max(0, current - 1));

  const submit = async (): Promise<Outcome> => {
    let outcome: Outcome = fail(
      new UseCaseError("Request validation failed", "REQUEST_VALIDATION_FAILED"),
    );
    if (submitting.current) return outcome;
    submitting.current = true;
    try {
      await form.handleSubmit(async (data) => {
        try {
          const created = await create.mutateAsync(buildBeneficiaryWizardRequest(data));
          reset();
          outcome = succeed(created);
        } catch (error) {
          outcome = fail(error);
        }
      })();
    } finally {
      submitting.current = false;
    }
    return outcome;
  };

  return {
    form,
    step,
    last,
    canContinue: !!stepId && areFieldsValid(schema, values, WIZARD_STEP_FIELDS[stepId]),
    isSubmitting: create.isPending,
    next,
    back,
    submit,
  };
}
